import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Package, CheckCircle, AlertTriangle, Plus, Undo2 } from 'lucide-react';
import { Client } from '@/types/shelter';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/hooks/use-toast';
import { logActivity } from '@/hooks/useActivityLog';
import UnbeddedPropertyDialog from '@/components/UnbeddedPropertyDialog';
import PropertyReturnDialog, { StoredItem, PropertyReturnPayload } from './PropertyReturnDialog';
import { formatNoteText } from '@/utils/formatNoteText';


interface ClientProfileBelongingsProps {
  client: Client;
}

interface PropertyEvent {
  id: string;
  title: string;
  content: string;
  category: string | null;
  created_at: string;
  authorName: string;
}

const parsePropertyContent = (content: string) => {
  try {
    return JSON.parse(content);
  } catch {
    return null;
  }
};

const buildStoredItems = (parsed: any): StoredItem[] => {
  if (!parsed) return [];
  const out: StoredItem[] = [];
  if (parsed.electronics) out.push({ key: 'electronics', label: 'Electronics', value: String(parsed.electronics) });
  if (parsed.documents) out.push({ key: 'documents', label: 'Documents', value: String(parsed.documents) });
  if (parsed.valuables) out.push({ key: 'valuables', label: 'Valuables', value: String(parsed.valuables) });
  if (parsed.additionalNotes) out.push({ key: 'additional', label: 'Additional', value: String(parsed.additionalNotes) });
  return out;
};

export const ClientProfileBelongings: React.FC<ClientProfileBelongingsProps> = ({ client }) => {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [returnTarget, setReturnTarget] = useState<PropertyEvent | null>(null);
  const queryClient = useQueryClient();

  const { data: events = [], isLoading } = useQuery({
    queryKey: ['client-belongings', client.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('notes')
        .select('id, title, content, category, created_at, created_by')
        .eq('client_id', client.id)
        .in('category', ['property', 'property_return'])
        .order('created_at', { ascending: false });
      if (error) throw error;

      const authorIds = [...new Set((data || []).map(n => n.created_by))];
      let profileMap = new Map<string, string>();
      if (authorIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, full_name')
          .in('id', authorIds);
        profileMap = new Map((profiles || []).map(p => [p.id, p.full_name || 'Unknown']));
      }

      return (data || []).map(n => ({
        id: n.id,
        title: n.title,
        content: n.content,
        category: n.category,
        created_at: n.created_at,
        authorName: profileMap.get(n.created_by) || 'Unknown',
      })) as PropertyEvent[];
    },
    enabled: !!client.id,
  });

  // Set of stored entry ids that already have a linked return
  const returnedStoredIds = useMemo(() => {
    const ids = new Set<string>();
    events.forEach((e) => {
      if (e.category === 'property_return') {
        const parsed = parsePropertyContent(e.content);
        if (parsed?.linkedStoredId) ids.add(parsed.linkedStoredId);
      }
    });
    return ids;
  }, [events]);

  const handleAddBelongings = async (notes: string) => {
    setIsAddDialogOpen(false);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase.from('notes').insert({
        title: 'Property Stored',
        content: notes,
        client_id: client.id,
        site_id: client.site_id || null,
        created_by: user.id,
        category: 'property',
        note_type: 'general',
      });

      await logActivity({
        actionType: 'property_return',
        actionDetails: {
          client_id: client.id,
          client_name: client.name,
          action: 'property_stored',
          timestamp: new Date().toISOString(),
        },
        siteId: client.site_id,
      });

      queryClient.invalidateQueries({ queryKey: ['client-belongings', client.id] });
      toast({ title: 'Belongings recorded', description: 'Property storage entry has been added.' });
    } catch (error: any) {
      toast({ variant: 'destructive', title: 'Error', description: error.message });
    }
  };

  const handleConfirmReturn = async (payload: PropertyReturnPayload) => {
    const target = returnTarget;
    setReturnTarget(null);
    if (!target) return;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const content = JSON.stringify({
        linkedStoredId: target.id,
        ...payload,
      });

      await supabase.from('notes').insert({
        title: 'Property Returned',
        content,
        client_id: client.id,
        site_id: client.site_id || null,
        created_by: user.id,
        category: 'property_return',
        note_type: 'general',
      });

      await logActivity({
        actionType: 'property_return',
        actionDetails: {
          client_id: client.id,
          client_name: client.name,
          action: 'property_returned',
          linked_stored_id: target.id,
          all_returned: payload.allReturned,
          missing_or_damaged: payload.missingOrDamaged,
          timestamp: new Date().toISOString(),
        },
        siteId: client.site_id,
      });

      queryClient.invalidateQueries({ queryKey: ['client-belongings', client.id] });
      toast({ title: 'Return recorded', description: 'Property return acknowledgement saved.' });
    } catch (error: any) {
      toast({ variant: 'destructive', title: 'Error', description: error.message });
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5 text-primary" />
            Belongings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5 text-primary" />
            Belongings
          </CardTitle>
          <Button size="sm" variant="outline" onClick={() => setIsAddDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-1" />
            Add Belongings
          </Button>
        </CardHeader>
        <CardContent>
          {events.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Package className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No property records found</p>
            </div>
          ) : (
            <div className="space-y-4">
              {events.map(event => {
                const isReturn = event.category === 'property_return';
                const parsed = parsePropertyContent(event.content);
                const dateStr = new Date(event.created_at).toLocaleString([], {
                  month: 'short', day: 'numeric', year: 'numeric',
                  hour: '2-digit', minute: '2-digit'
                });
                const alreadyReturned = !isReturn && returnedStoredIds.has(event.id);

                return (
                  <div key={event.id} className={`p-4 rounded-lg border ${isReturn ? 'bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-800' : 'bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-800'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      {isReturn ? (
                        <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                      ) : (
                        <Package className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                      )}
                      <span className="font-medium text-sm">{event.title}</span>
                      <span className="text-xs text-muted-foreground ml-auto">{dateStr}</span>
                      {!isReturn && (
                        alreadyReturned ? (
                          <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">
                            Returned
                          </span>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 px-2"
                            onClick={() => setReturnTarget(event)}
                          >
                            <Undo2 className="h-3.5 w-3.5 mr-1" />
                            Mark Returned
                          </Button>
                        )
                      )}
                    </div>

                    {isReturn && parsed ? (
                      <div className="text-sm space-y-1 pl-6">
                        {parsed.allReturned ? (
                          <p className="text-green-700 dark:text-green-300">All items returned</p>
                        ) : (
                          <>
                            {parsed.returnedItems?.length > 0 && (
                              <p className="text-muted-foreground">
                                <span className="font-medium">Returned:</span> {parsed.returnedItems.join('; ')}
                              </p>
                            )}
                            {parsed.notReturned?.length > 0 && (
                              <p className="text-amber-700 dark:text-amber-300 flex items-center gap-1">
                                <AlertTriangle className="h-3 w-3" />
                                <span className="font-medium">Not returned:</span> {parsed.notReturned.join(', ')}
                              </p>
                            )}
                          </>
                        )}
                        {parsed.missingOrDamaged && parsed.missingOrDamaged !== 'None' && (
                          <p className="text-destructive flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" />
                            <span className="font-medium">Missing/Damaged:</span> {parsed.missingOrDamaged}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground">Released by: {event.authorName}</p>
                      </div>
                    ) : !isReturn && parsed ? (
                      <div className="text-sm space-y-1 pl-6">
                        {parsed.propertyLocation && <p><span className="font-medium">Location:</span> {parsed.propertyLocation}</p>}
                        {parsed.electronics && <p><span className="font-medium">Electronics:</span> {parsed.electronics}</p>}
                        {parsed.documents && <p><span className="font-medium">Documents:</span> {parsed.documents}</p>}
                        {parsed.valuables && <p><span className="font-medium">Valuables:</span> {parsed.valuables}</p>}
                        {parsed.additionalNotes && <p><span className="font-medium">Notes:</span> {parsed.additionalNotes}</p>}
                        <p className="text-xs text-muted-foreground">by {event.authorName}</p>
                      </div>
                    ) : (
                      <div className="text-sm pl-6">
                        <p className="whitespace-pre-wrap">{formatNoteText(event.content)}</p>
                        <p className="text-xs text-muted-foreground mt-1">by {event.authorName}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <UnbeddedPropertyDialog
        isOpen={isAddDialogOpen}
        onClose={() => setIsAddDialogOpen(false)}
        onConfirm={handleAddBelongings}
        clientName={client.name}
      />

      {returnTarget && (
        <PropertyReturnDialog
          isOpen={!!returnTarget}
          onClose={() => setReturnTarget(null)}
          onConfirm={handleConfirmReturn}
          clientName={client.name}
          storedAtLabel={new Date(returnTarget.created_at).toLocaleString([], {
            month: 'short', day: 'numeric', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
          })}
          items={buildStoredItems(parsePropertyContent(returnTarget.content))}
        />
      )}
    </>
  );
};
